import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, ExternalLink, Globe } from "lucide-react";
import { toast } from "sonner";

export default function SitemapManager() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // New Rule Form
  const [newPath, setNewPath] = useState("/");
  const [newPriority, setNewPriority] = useState("0.8");
  const [newFreq, setNewFreq] = useState("weekly");

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const res = await axios.get(`${API}/seo/sitemaps`, { withCredentials: true });
      setRules(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch sitemap rules");
    } finally {
      setLoading(false);
    }
  };

  const handleAddRule = async () => {
    if (!newPath) return toast.error("Path is required");
    
    setSaving(true);
    try {
      await axios.post(`${API}/seo/sitemaps`, {
        path: newPath,
        priority: parseFloat(newPriority),
        changefreq: newFreq
      }, { withCredentials: true });
      
      toast.success("Sitemap rule added!");
      setNewPath("/");
      setNewPriority("0.8");
      setNewFreq("weekly");
      fetchRules();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add rule");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (path) => {
    try {
      await axios.delete(`${API}/seo/sitemaps/${encodeURIComponent(path)}`, { withCredentials: true });
      toast.success("Rule deleted");
      fetchRules();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete rule");
    }
  };

  const handlePingGoogle = () => {
    const sitemapUrl = `https://jamadhan.com/sitemap.xml`;
    window.open(`https://www.google.com/ping?sitemap=${encodeURIComponent(sitemapUrl)}`, "_blank");
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600 dark:text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            Sitemap XML Generator
          </h3>
          <p className="text-sm text-gray-500 dark:text-[#94A3B8]">
            Your sitemap is automatically generated at <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium">/sitemap.xml</a>. Add custom rules below to override default priority and change frequency for specific pages.
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => window.open('/sitemap.xml', '_blank')}
            variant="outline"
            className="border-gray-200 dark:border-white/10 text-gray-900 dark:text-white hover:bg-gray-100 dark:bg-white/5 rounded-xl"
          >
            <ExternalLink className="w-4 h-4 mr-2" /> View XML
          </Button>
          <Button 
            onClick={handlePingGoogle}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl shadow-xs"
          >
            Ping Google
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-[#0B101A] border border-gray-100 dark:border-white/[0.05] rounded-xl p-5 shadow-xs">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Add Custom Rule</h4>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1 space-y-1.5">
            <label className="text-xs text-gray-500 dark:text-[#94A3B8]">URL Path</label>
            <Input 
              value={newPath}
              onChange={e => setNewPath(e.target.value)}
              placeholder="/about"
              className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-xl"
            />
          </div>
          <div className="w-full md:w-32 space-y-1.5">
            <label className="text-xs text-gray-500 dark:text-[#94A3B8]">Priority (0.0 - 1.0)</label>
            <Input 
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={newPriority}
              onChange={e => setNewPriority(e.target.value)}
              className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-xl"
            />
          </div>
          <div className="w-full md:w-40 space-y-1.5">
            <label className="text-xs text-gray-500 dark:text-[#94A3B8]">Change Frequency</label>
            <Select value={newFreq} onValueChange={setNewFreq}>
              <SelectTrigger className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-xl">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-[#0B101A] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white">
                <SelectItem value="always">Always</SelectItem>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
                <SelectItem value="never">Never</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleAddRule} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl shadow-xs md:w-auto w-full">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Save Rule
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-[#0B101A] border border-gray-100 dark:border-white/[0.05] rounded-xl overflow-hidden shadow-xs">
        <div className="overflow-x-auto w-full">
          <table className="w-full text-left border-collapse min-w-[500px]">
            <thead>
              <tr className="border-b border-gray-100 dark:border-white/[0.05] bg-gray-50 dark:bg-white/[0.02]">
                <th className="p-4 text-xs font-semibold text-gray-500 dark:text-[#94A3B8] w-full">URL Path</th>
                <th className="p-4 text-xs font-semibold text-gray-500 dark:text-[#94A3B8] whitespace-nowrap">Priority</th>
                <th className="p-4 text-xs font-semibold text-gray-500 dark:text-[#94A3B8] whitespace-nowrap">Frequency</th>
                <th className="p-4 text-xs font-semibold text-gray-500 dark:text-[#94A3B8] whitespace-nowrap text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-gray-500 dark:text-[#94A3B8] text-sm">
                    No custom rules defined. Using automatic defaults.
                  </td>
                </tr>
              ) : (
                rules.map(rule => (
                  <tr key={rule._id} className="border-b border-gray-100 dark:border-white/[0.05] hover:bg-gray-50 dark:bg-white/[0.01]">
                    <td className="p-4 text-sm text-gray-900 dark:text-white font-mono">{rule.path}</td>
                    <td className="p-4 text-sm text-emerald-600 dark:text-emerald-400 font-mono font-medium">{rule.priority}</td>
                    <td className="p-4 text-sm text-gray-500 dark:text-[#94A3B8] capitalize">{rule.changefreq}</td>
                    <td className="p-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRule(rule.path)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
